import {useInjection} from "inversify-react";
import {useCallback, useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {
    CategoryApi,
    CategoryDto,
    DiscountCodeApi,
    DiscountCodeBulkCreateDto,
    DiscountCodeCreateDto,
    DiscountCodeUpdateDto,
    ProductApi,
    ProductDto
} from "../../../api";
import ApiService from "../../../services/apiService";
import {loadingSpin, RoutingPaths} from "../../routing/routingContainer";
import DiscountCodeForm from "./discountCodeForm";
import {message} from "antd";
import getErrorDataMessage from "../../../helpers/getErrorDataMessage";

export default function DiscountCodeFormAddContainer() {
    const apiService = useInjection(ApiService);
    const [discountCodeToEdit, setDiscountCodeToEdit] = useState<DiscountCodeUpdateDto | null>(null);
    const [products, setProducts] = useState<ProductDto[]>([]);
    const [categories, setCategories] = useState<CategoryDto[]>([]);
    const [optionsLoaded, setOptionsLoaded] = useState(false);
    const navigate = useNavigate();
    const {id} = useParams();

    const onSubmit = useCallback(async (values: DiscountCodeCreateDto | DiscountCodeUpdateDto) => {
        try {
            if (!id) {
                await apiService.getApi(DiscountCodeApi).apiDiscountCodePost(values);
            } else {
                await apiService.getApi(DiscountCodeApi).apiDiscountCodeIdPut(+id, values)
            }

            navigate(RoutingPaths.discountCodeTable.route);
        } catch (e: any) {
            const errorMessage = getErrorDataMessage(e.response?.data);

            if (errorMessage.includes("CodeRepeated") || errorMessage.includes("Kod rabatowy o tej nazwie już istnieje.")) {
                message.error("Podany kod już istanieje, podaj inny");
                return;
            }
            console.log(e);
            return e.response?.data;
        }
    }, [apiService, id]);

    const onBulkSubmit = useCallback(async (values: DiscountCodeCreateDto, codes: string[]) => {
        try {
            await apiService.getApi(DiscountCodeApi).apiDiscountCodeBulkPost({
                discountCode: values,
                codes
            } as DiscountCodeBulkCreateDto);

            navigate(RoutingPaths.discountCodeTable.route);
        } catch (e: any) {
            const errorMessage = getErrorDataMessage(e.response?.data);

            if (errorMessage.includes("CodeRepeated") || errorMessage.includes("Kod rabatowy o tej nazwie już istnieje.") || errorMessage.includes("już istnieje")) {
                message.error("Część kodów już istnieje, zmień format albo prefix");
                return;
            }

            console.log(e);
            return e.response?.data;
        }
    }, [apiService, navigate]);

    const getDiscountCodeToEdit = useCallback(async () => {
        const [productsResponse, categoriesResponse] = await Promise.all([
            apiService.getApi(ProductApi).apiProductGet(),
            apiService.getApi(CategoryApi).apiCategoryGet()
        ]);

        setProducts(productsResponse.data);
        setCategories(categoriesResponse.data);
        setOptionsLoaded(true);

        if (!id) {
            return;
        }

        const response = await apiService.getApi(DiscountCodeApi).apiDiscountCodeIdGet(+id);
        setDiscountCodeToEdit(response.data);
    }, [apiService, id]);

    useEffect(() => {
        getDiscountCodeToEdit();
    }, [getDiscountCodeToEdit]);

    if (id && !discountCodeToEdit) {
        return loadingSpin;
    }

    if (!optionsLoaded) {
        return loadingSpin;
    }

    return <>
        <DiscountCodeForm
            onSubmit={onSubmit}
            onBulkSubmit={onBulkSubmit}
            saveButtonTitle={"Zapisz"}
            initialValues={discountCodeToEdit!}
            products={products}
            categories={categories}
            allowBulkCreate={!id}
        />
    </>
}
